import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Shield, AlertTriangle, XCircle, Clock, Loader2, RotateCcw, Receipt, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const features = [
  "3 richieste annuali",
  "Assistenza utenze (luce, gas, fibra)",
  "Supporto credito riservato agli abbonati",
  "Area Clienti dedicata",
  "Assistenza durante tutto l'anno",
];

const STATUS_UI = {
  "Attivo": { color: "bg-emerald-100 text-emerald-700", icon: Check },
  "Non attivo": { color: "bg-slate-100 text-slate-500", icon: Shield },
  "Pagamento in elaborazione": { color: "bg-amber-100 text-amber-700", icon: Loader2 },
  "Pagamento fallito": { color: "bg-red-100 text-red-700", icon: AlertTriangle },
  "Annullato": { color: "bg-slate-100 text-slate-500", icon: XCircle },
  "Scaduto": { color: "bg-slate-100 text-slate-500", icon: Clock },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—");

const RECESSO_TEXT = "Richiedo espressamente l'avvio immediato del servizio durante il periodo di recesso";

function RecessoBox({ checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 text-sm text-slate-700">
      <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#10B981]" />
      <span>{RECESSO_TEXT}</span>
    </label>
  );
}

export default function PianoCliente() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(user?.subscription || {});
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [recesso, setRecesso] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const loadPlan = useCallback(async () => {
    try {
      const { data } = await api.get("/plan");
      setSub(data);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/payments");
      setPayments(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  // Return from Stripe checkout: confirm payment server-side via polling, then activate.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const kind = params.get("kind");
    if (params.get("canceled")) {
      toast.info("Pagamento annullato.");
      params.delete("canceled"); setParams(params, { replace: true });
      return;
    }
    if (!sessionId) return;
    let cancelled = false;
    setVerifying(true);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (cancelled) return;
          await refreshUser();
          await loadPlan();
          toast.success(kind === "extra"
            ? "Pagamento confermato: hai 1 Richiesta Extra disponibile!"
            : "Pagamento confermato: Piano ViviTranquillo attivo!");
          setVerifying(false);
          params.delete("session_id"); params.delete("kind"); setParams(params, { replace: true });
          return;
        }
      } catch { /* ignore */ }
      if (tries >= 8) {
        if (!cancelled) {
          setVerifying(false);
          toast.info("Stiamo confermando il pagamento. Lo stato si aggiornerà a breve.");
          params.delete("session_id"); params.delete("kind"); setParams(params, { replace: true });
        }
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (years) => {
    if (!recesso) { toast.error("Devi richiedere l'avvio immediato del servizio per procedere."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, years, immediate_start: true });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  const startExtraCheckout = async () => {
    if (!recesso) { toast.error("Devi richiedere l'avvio immediato del servizio per procedere."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/extra-checkout", { origin_url: window.location.origin, immediate_start: true });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  const cancelRenew = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/plan/cancel");
      setSub(data);
      await refreshUser();
      setConfirmCancelOpen(false);
      toast.success("Rinnovo automatico disattivato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };

  const resumeRenew = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/plan/resume");
      setSub(data);
      await refreshUser();
      toast.success("Rinnovo automatico riattivato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };

  const status = sub.status || (sub.active ? "Attivo" : "Non attivo");
  const ui = STATUS_UI[status] || STATUS_UI["Non attivo"];
  const StatusIcon = ui.icon;
  const planYears = sub.plan_years || 1;
  const durataLabel = planYears === 1 ? "1 Anno" : `${planYears} Anni`;
  const priceLabel = sub.price || 49;
  const includedUsed = sub.requests_used || 0;
  const includedTotal = sub.requests_total || 3;
  const extraTotal = sub.extra_total || 0;
  const extraUsed = sub.extra_used || 0;
  const extraAvailable = Math.max(0, extraTotal - extraUsed);
  const includedExhausted = sub.active && includedUsed >= includedTotal;

  return (
    <div data-testid="piano-cliente-page" className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Il tuo Piano</h1>
        <p className="mt-1 text-slate-600">Gestisci il tuo abbonamento ViviTranquillo.</p>
      </div>

      {verifying && (
        <div data-testid="piano-verifying" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <Loader2 className="h-5 w-5 animate-spin" /> Stiamo confermando il tuo pagamento con Stripe...
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between bg-[#0A192F] p-6">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-[#10B981]" />
            <div>
              <p className="font-display text-lg font-bold text-white" data-testid="piano-name">Piano ViviTranquillo {sub.active ? durataLabel : ""}</p>
              <p className="text-sm text-slate-400">€{sub.active ? priceLabel : 49} {sub.active ? `· ${durataLabel}` : "/anno"} · rinnovo automatico</p>
            </div>
          </div>
          <span data-testid="piano-status" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold ${ui.color}`}>
            <StatusIcon className="h-3.5 w-3.5" /> {status}
          </span>
        </div>

        <div className="p-6 lg:p-8">
          {sub.active && (
            <>
              {!sub.cancel_at_period_end && (
                <div data-testid="piano-autorenew-active" className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
                  <RotateCcw className="h-4 w-4" /> Rinnovo automatico attivo · €{priceLabel} ogni {durataLabel.toLowerCase()}
                </div>
              )}
              <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Anno di copertura</p>
                  <p className="mt-1 font-display text-2xl font-bold text-[#0A192F]" data-testid="piano-coverage-year">Anno {sub.coverage_year || 1} di {planYears}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Richieste (anno corrente)</p>
                  <p className="mt-1 font-display text-2xl font-bold text-[#0A192F]" data-testid="piano-requests">{sub.requests_used || 0} di {sub.requests_total || 3}</p>
                  <p className="text-xs text-slate-400">utilizzate</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Inizio → Fine periodo</p>
                  <p className="mt-1 font-display text-sm font-bold text-[#0A192F]">{fmtDate(sub.started_at)} → {fmtDate(sub.expires_at)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">{sub.cancel_at_period_end ? "Scade il" : "Prossimo rinnovo"}</p>
                  <p className="mt-1 font-display text-base font-bold text-[#0A192F]">{fmtDate(sub.cancel_at_period_end ? sub.expires_at : sub.renews_at)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Rinnovo automatico</p>
                  <p className="mt-1 font-display text-base font-bold text-[#0A192F]">{sub.cancel_at_period_end ? "Non attivo" : "Attivo"}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Ultimo pagamento</p>
                  <p className="mt-1 font-display text-base font-bold text-[#0A192F]">{sub.last_payment_status === "paid" ? "Riuscito" : sub.last_payment_status === "failed" ? "Fallito" : "—"}</p>
                </div>
              </div>

              <div data-testid="piano-extra-section" className="mb-6 rounded-xl border border-slate-200 bg-[#F8FAFC] p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-xl">
                    <p className="font-display text-base font-bold text-[#0A192F]">Richiesta Extra – €19,90</p>
                    <p className="mt-1 text-sm text-slate-600">Hai esaurito le 3 richieste incluse nel tuo abbonamento? Acquista 1 richiesta di assistenza aggiuntiva a €19,90. Puoi ripetere l'acquisto ogni volta che ne hai bisogno, durante la validità dell'abbonamento.</p>
                    {extraTotal > 0 && (
                      <p data-testid="piano-extra-count" className="mt-2 text-sm font-semibold text-[#0A192F]">{extraUsed} di {extraTotal} extra utilizzate · {extraAvailable} disponibili</p>
                    )}
                  </div>
                  <button data-testid="piano-buy-extra" onClick={startExtraCheckout} disabled={loading || !recesso} className="rounded-full bg-[#F97316] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#ea6a0c] disabled:opacity-60">
                    {loading ? "..." : "Acquista Richiesta Extra · €19,90"}
                  </button>
                </div>
                <div className="mt-4">
                  <RecessoBox testid="piano-recesso-extra" checked={recesso} onChange={setRecesso} />
                </div>
                {includedExhausted && extraAvailable === 0 && (
                  <div data-testid="piano-extra-message" className="mt-4 rounded-lg border border-[#F97316]/30 bg-[#F97316]/5 p-4 text-sm text-[#0A192F]">
                    Hai utilizzato le 3 richieste incluse nel tuo Piano. Puoi continuare a utilizzare ViviTranquillo acquistando richieste extra a €19,90 ciascuna.
                  </div>
                )}
              </div>

              {sub.cancel_at_period_end && (
                <div data-testid="piano-cancel-notice" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  Il tuo Piano resterà attivo fino al <strong>{fmtDate(sub.expires_at)}</strong>. Non verrà effettuato alcun nuovo addebito.
                </div>
              )}
            </>
          )}

          {status === "Pagamento fallito" && (
            <div data-testid="piano-failed-notice" className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Non siamo riusciti a rinnovare il tuo Piano. Aggiorna il metodo di pagamento per continuare a usare i servizi.
            </div>
          )}

          <ul className="space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-[#0A192F]">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#10B981]"><Check className="h-4 w-4 text-white" /></span>
                <span className="font-medium">{f}</span>
              </li>
            ))}
          </ul>

          {!sub.active ? (
            <div className="mt-6 space-y-4">
              <RecessoBox testid="piano-recesso-activate" checked={recesso} onChange={setRecesso} />
              <div data-testid="piano-select" className="grid gap-4 sm:grid-cols-3">
              {[
                { years: 1, price: 49, label: "1 Anno", sub: "12 mesi · 3 richieste", save: null },
                { years: 2, price: 89, label: "2 Anni", sub: "24 mesi · 3 richieste/anno", save: 9, best: true },
                { years: 3, price: 129, label: "3 Anni", sub: "36 mesi · 3 richieste/anno", save: 18 },
              ].map((o) => (
                <div key={o.years} data-testid={`piano-option-${o.years}`} className={`relative flex flex-col rounded-2xl border-2 p-5 ${o.best ? "border-[#10B981] bg-[#10B981]/5" : "border-slate-200"}`}>
                  {o.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#10B981] px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Più conveniente</span>}
                  <p className="font-display text-lg font-bold text-[#0A192F]">{o.label}</p>
                  <p className="mt-1 font-display text-3xl font-extrabold text-[#0A192F]">€{o.price}</p>
                  <p className="mt-1 text-xs text-slate-500">{o.sub}</p>
                  {o.save ? <p className="mt-2 inline-block rounded-full bg-[#F97316]/10 px-2.5 py-1 text-xs font-bold text-[#F97316]">Risparmi €{o.save}</p> : <p className="mt-2 text-xs text-transparent">-</p>}
                  <button data-testid={`piano-activate-${o.years}`} onClick={() => startCheckout(o.years)} disabled={loading || !recesso} className={`mt-4 w-full rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${o.best ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#0A192F] hover:bg-slate-800"}`}>
                    {loading ? "..." : `Attiva · €${o.price}`}
                  </button>
                </div>
              ))}
              </div>
            </div>
          ) : sub.cancel_at_period_end ? (
            <button data-testid="piano-resume-btn" onClick={resumeRenew} disabled={loading} className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#10B981] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
              <RotateCcw className="h-4 w-4" /> {loading ? "Attendere..." : "Riattiva il rinnovo automatico"}
            </button>
          ) : (
            <button data-testid="piano-cancel-btn" onClick={() => setConfirmCancelOpen(true)} disabled={loading} className="mt-8 w-full rounded-full border-2 border-slate-200 px-8 py-3.5 font-semibold text-slate-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-60">
              {loading ? "Attendere..." : "Disattiva rinnovo automatico"}
            </button>
          )}
          <p className="mt-4 text-center text-xs text-slate-400">
            Pagamento sicuro gestito da Stripe. I preventivi assicurativi restano gratuiti e separati.
          </p>
        </div>
      </div>

      {/* Pagamenti e ricevute */}
      <div data-testid="piano-payments-section" className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-[#10B981]" />
          <h2 className="font-display text-xl font-bold text-[#0A192F]">Pagamenti e ricevute</h2>
        </div>
        {payments.length === 0 ? (
          <p data-testid="piano-payments-empty" className="mt-4 text-sm text-slate-400">
            Nessun pagamento ancora. Le ricevute compariranno qui dopo l'attivazione del Piano.
          </p>
        ) : (
          <div className="mt-5 space-y-3">
            {payments.map((p) => (
              <div key={p.id} data-testid={`piano-payment-${p.id}`} className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-display font-bold text-[#0A192F]">
                    € {Number(p.amount || 0).toFixed(2)}
                    <span className="ml-2 text-xs font-medium text-slate-400">{p.currency}</span>
                  </p>
                  <p className="text-xs text-slate-500">{fmtDate(p.created_at)} · Rif. {p.reference}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {p.status === "paid" ? "Pagato" : p.status}
                  </span>
                  {p.hosted_invoice_url && (
                    <a data-testid={`piano-receipt-link-${p.id}`} href={p.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 px-4 py-1.5 text-xs font-semibold text-[#0A192F] transition-colors hover:border-[#10B981]">
                      Ricevuta <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={confirmCancelOpen} onOpenChange={(o) => { if (!loading) setConfirmCancelOpen(o); }}>
        <AlertDialogContent data-testid="piano-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="piano-cancel-dialog-title">Vuoi rinunciare alla tua tutela alla scadenza?</AlertDialogTitle>
            <AlertDialogDescription data-testid="piano-cancel-dialog-text">
              Restando con ViviTranquillo, hai qualcuno dalla tua parte quando serve. Se disattivi il rinnovo, dal {fmtDate(sub.expires_at || sub.renews_at)} non avrai più accesso alla tutela e all'assistenza incluse nel piano. Fino a quella data il servizio resta attivo e non cambia nulla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="piano-cancel-dialog-keep" disabled={loading}>Mantieni la mia tutela</AlertDialogCancel>
            <AlertDialogAction
              data-testid="piano-cancel-dialog-confirm"
              disabled={loading}
              onClick={(e) => { e.preventDefault(); cancelRenew(); }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {loading ? "Attendere..." : "Conferma disattivazione"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
